/**
 * COMPONENT: EmailTemplateSelector
 * Sélecteur de modèle d'email réutilisable (V4 R8 retours phase B).
 * Utilisé par SendEmailModal et SendBulkEmailModal.
 *
 * Comportement :
 *  - dropdown alimenté par useEmailTemplates (passé en props pour rester contrôlé)
 *  - "Aucun" remet le state à null sans toucher au form (pas d'écrasement)
 *  - sélection d'un template → onChange(template) ; le parent applique
 *    setValue('subject') / setValue('body') (avec confirm si écrasement)
 *  - Bug 3 fix-tests-empiriques (R8) : si templates.length === 0, afficher
 *    un état explicite "Aucun modèle disponible — créer dans Admin → Modèles
 *    d'email" plutôt qu'un return null silencieux. Le silent skip donnait
 *    l'impression que le composant n'était pas monté (faux bug R008).
 */

import type { EmailTemplate } from '@/types/emailTemplate';

interface EmailTemplateSelectorProps {
  /** ID du template actuellement sélectionné, ou '' pour aucun. */
  value: string;
  /** Liste des templates disponibles (typiquement issue de useEmailTemplates). */
  templates: EmailTemplate[];
  /** Callback : null = "Aucun", sinon le template sélectionné. */
  onChange: (template: EmailTemplate | null) => void;
  /** ID HTML du <select> pour le htmlFor du <label>. Défaut généré. */
  selectId?: string;
}

export function EmailTemplateSelector({
  value,
  templates,
  onChange,
  selectId = 'email-template-select',
}: EmailTemplateSelectorProps) {
  // Bug 3 fix-tests-empiriques (R8) : empty state explicite — observability,
  // pas de silent skip qui ferait croire que le composant n'est pas monté.
  if (templates.length === 0) {
    return (
      <div data-testid="email-template-selector-empty">
        <label className="block text-sm font-medium text-zinc-700 mb-2">Modèle (optionnel)</label>
        <div className="w-full px-3 py-2 text-sm border border-dashed border-zinc-300 rounded-md bg-zinc-50 text-zinc-600">
          Aucun modèle d'e-mail disponible.
        </div>
        <p className="mt-1 text-xs text-zinc-500">
          Créez vos modèles dans Admin → Modèles d'email pour pré-remplir sujet et corps.
        </p>
      </div>
    );
  }

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = e.target.value;
    if (!id) {
      onChange(null);
      return;
    }
    const template = templates.find((t) => t.id === id) || null;
    onChange(template);
  };

  return (
    <div>
      <label htmlFor={selectId} className="block text-sm font-medium text-zinc-700 mb-2">
        Modèle (optionnel)
      </label>
      <select
        id={selectId}
        data-testid="email-template-select"
        value={value}
        onChange={handleChange}
        className="w-full px-3 py-2 text-sm border border-zinc-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-zinc-900"
      >
        <option value="">Aucun — rédiger librement</option>
        {templates.map((template) => (
          <option key={template.id} value={template.id}>
            {template.name}
          </option>
        ))}
      </select>
      <p className="mt-1 text-xs text-zinc-500">
        Sélectionner un modèle préremplit le sujet et le corps. Les modèles sont gérés dans Admin →
        Modèles d'email.
      </p>
    </div>
  );
}
